import React, { useState, useEffect, useRef } from 'react';
import { ConfiguratorState, Dimensions } from '../../types';
import { getFixedDimensions } from '../../config/priceConfigFixed';
import { getComfortDimensions } from '../../config/priceConfigComfort';
import { getWalkOnDimensions } from '../../config/priceConfigWalkOn';
import { getAccessDimensions } from '../../config/priceConfigAccess';
import PerspectiveViewer, { productKindFromName } from '../UI/PerspectiveViewer';
import OpeningExplainDialog from '../UI/OpeningExplainDialog';
import DimensionInput from '../UI/DimensionInput';
import { debug, debugError } from '../../utils/debug';
import { productCodeFromName } from '../../services/priceService';

interface SizeSelectionProps {
  state: ConfiguratorState;
  updateState: (updates: Partial<ConfiguratorState>) => void;
  onNext: () => void;
  onBack?: () => void;
}

interface ProductDimensions {
  minWidth?: number;
  maxWidth?: number;
  minLength?: number;
  maxLength?: number;
  minDiameter?: number;
  maxDiameter?: number;
  stepSize?: number;
  minHeight?: number;
  maxHeight?: number;
  maxArea?: number;
}

const CIRCULAR_FALLBACK = { MIN: 600, MAX: 2000, STEP: 100 };

const MEASURE_MODE_COPY = {
  order: {
    lead: 'Fenstermaß ist das Bestellmaß.',
    body:
      'Das ist die Größe des fertigen Fensters: was wir bauen und was auf der Bestellung steht. Wenn Sie wissen, wie groß das Fenster sein soll, geben Sie diese Zahlen ein.',
    why:
      'Die Rohbaulichte in der Decke ist 30 mm größer, 15 mm je Seite. Dieser Rand bleibt frei, damit die Deckenlaibung verkleidet werden kann: die sichtbare Kante der Öffnung von unten.',
    resultLabel: 'Rohbaulichte',
    resultHint:
      '30 mm größer. 15 mm je Seite für die Verkleidung der Deckenlaibung.',
  },
  opening: {
    lead: 'Die Rohbaulichte ist die Öffnung in der Decke.',
    body:
      'Wenn die Aussparung schon feststeht, geben Sie sie hier ein, nicht das Fenster. Wir ziehen 30 mm ab und fertigen das Bestellmaß, also das Fenstermaß, 15 mm je Seite kleiner.',
    why:
      'Die 15 mm je Seite bleiben frei, damit die Deckenlaibung verkleidet werden kann: die sichtbare Kante der Öffnung von unten.',
    resultLabel: 'Bestellmaß',
    resultHint:
      'Fenstermaß. 30 mm kleiner als die Rohbaulichte. Das fertigen wir, das bestellen Sie.',
  },
} as const;

/** Rohbauöffnung ↔ Bestellmaß mode picker. Same visual grammar as the
 *  Riss/Foto toggle in the header \u2014 underline swaps to vermillion for
 *  the active mode. */
function InputModeToggle({
  mode,
  onChange,
}: {
  mode: 'order' | 'opening';
  onChange: (m: 'order' | 'opening') => void;
}) {
  const item = (m: 'order' | 'opening', title: string, sub: string) => {
    const active = mode === m;
    return (
      <button
        key={m}
        type="button"
        role="radio"
        aria-checked={active}
        onClick={() => onChange(m)}
        className={`sv-measure-mode ${active ? 'is-active' : ''}`}
      >
        <span className="sv-measure-mode-title">{title}</span>
        <span className="sv-measure-mode-sub">{sub}</span>
      </button>
    );
  };
  return (
    <div
      role="radiogroup"
      aria-label="Wonach Sie messen"
      aria-describedby="sv-measure-mode-explain"
      className="sv-measure-modes"
    >
      {item('order', 'Fenstermaß', 'das Bestellmaß')}
      {item('opening', 'Rohbaulichte', 'Öffnung in der Decke')}
    </div>
  );
}

function MeasureModeExplain({
  mode,
  onOpenDrawing,
}: {
  mode: 'order' | 'opening';
  onOpenDrawing: () => void;
}) {
  const copy = MEASURE_MODE_COPY[mode];
  return (
    <div
      id="sv-measure-mode-explain"
      className="sv-measure-explain"
      aria-live="polite"
    >
      <p className="sv-measure-explain-lead">{copy.lead}</p>
      <p className="sv-measure-explain-body">{copy.body}</p>
      <p className="sv-measure-explain-body">{copy.why}</p>
      <button
        type="button"
        className="sv-measure-explain-action"
        onClick={onOpenDrawing}
      >
        Fotos und Zeichnung: Bestellmaß und Rohbaulichte
      </button>
    </div>
  );
}

export default function SizeSelection({
  state,
  updateState,
  onNext,
  onBack,
}: SizeSelectionProps) {
  const [inputMode, setInputMode] = useState<'order' | 'opening'>('order');
  const [explainOpen, setExplainOpen] = useState(false);
  const [productDimensions, setProductDimensions] =
    useState<ProductDimensions | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const FIXED_DIMENSIONS = getFixedDimensions();

  useEffect(() => {
    if (!state.product) {
      setLoading(false);
      setProductDimensions(null);
      setError(null);
      return;
    }
    const controller = new AbortController();
    let cancelled = false;

    (async () => {
      try {
        setLoading(true);
        setError(null);
        const productId = productCodeFromName(state.product);
        const response = await fetch(
          `/wp-json/skyvision/v1/products/${productId}`,
          { signal: controller.signal },
        );
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const data = await response.json();
        if (cancelled) return;

        setProductDimensions({
          minWidth: data.dimensions?.minWidth || data.min_width,
          maxWidth: data.dimensions?.maxWidth || data.max_width,
          minLength: data.dimensions?.minLength || data.min_length,
          maxLength: data.dimensions?.maxLength || data.max_length,
          minDiameter: data.dimensions?.minDiameter || data.min_diameter,
          maxDiameter: data.dimensions?.maxDiameter || data.max_diameter,
          stepSize: Number(data.step_size) > 0 ? Number(data.step_size) : undefined,
          minHeight: data.min_height,
          maxHeight: data.max_height,
          maxArea: data.dimensions?.maxArea ?? data.maxArea ?? data.max_area,
        });
      } catch (err) {
        if (cancelled) return;
        if ((err as { name?: string })?.name === 'AbortError') return;
        debugError('Fehler beim Laden der Produktdimensionen:', err);
        setError('Produktdaten nicht geladen — Standardwerte werden verwendet.');
        setProductDimensions(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [state.product]);

  // Auto-adjust height when glazing changes.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  });
  useEffect(() => {
    const current = stateRef.current;
    if (!current.glassType || !current.dimensions) return;
    const isTriple = current.glassType === 'triple';
    const minHeight = isTriple
      ? FIXED_DIMENSIONS.MIN_HEIGHT_TRIPLE
      : FIXED_DIMENSIONS.MIN_HEIGHT_DOUBLE;
    if (current.dimensions.height < minHeight) {
      const nextDim = { ...current.dimensions, height: minHeight };
      updateState({ dimensions: nextDim });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.glassType]);

  if (!state?.dimensions) {
    return (
      <div className="py-16 max-w-lg">
        <p className="sv-caps text-[11px] text-ink-3 mb-3">
          Kein Konfigurationskontext
        </p>
        <p className="sv-serif text-ink-2 mb-4">
          Bitte gehen Sie zurück und wählen Sie ein Produkt aus, damit wir die
          Maße festlegen können.
        </p>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="sv-caps-sm text-[11px] text-ink underline decoration-rubric decoration-2 underline-offset-4"
          >
            ← Zurück
          </button>
        )}
      </div>
    );
  }

  const isCircular =
    state.product === 'SkyVision CIRCULAR' ||
    state.product === 'SkyVision CIRCULAR WALK-ON';
  const isFixed = state.product === 'SkyVision FIXED';
  const isComfort = state.product === 'SkyVision COMFORT';
  const isAccess = state.product === 'SkyVision ACCESS';
  const isWalkOn = state.product === 'SkyVision WALKON';
  const isLinear = state.product === 'SkyVision LINEAR';
  const isTriple = state.glassType === 'triple';

  const CIRC = productDimensions?.minDiameter
    ? {
        MIN: productDimensions.minDiameter,
        MAX: productDimensions.maxDiameter || CIRCULAR_FALLBACK.MAX,
        STEP: productDimensions.stepSize || CIRCULAR_FALLBACK.STEP,
      }
    : CIRCULAR_FALLBACK;

  const COMFORT_DIMENSIONS = isComfort ? getComfortDimensions() : null;
  const ACCESS_LIMITS = isAccess ? getAccessDimensions() : null;
  const walkOnDefaults = isWalkOn ? getWalkOnDimensions() : null;
  const accessFallback = ACCESS_LIMITS
    ? {
        MIN_WIDTH: ACCESS_LIMITS.MIN_WIDTH,
        MAX_WIDTH: ACCESS_LIMITS.MAX_WIDTH,
        MIN_LENGTH: ACCESS_LIMITS.MIN_LENGTH,
        MAX_LENGTH: ACCESS_LIMITS.MAX_LENGTH,
        MAX_HEIGHT: ACCESS_LIMITS.MAX_HEIGHT,
        MAX_AREA: ACCESS_LIMITS.MAX_AREA,
        MIN_HEIGHT_DOUBLE: ACCESS_LIMITS.MIN_HEIGHT,
        MIN_HEIGHT_TRIPLE: ACCESS_LIMITS.MIN_HEIGHT,
      }
    : null;
  const fallbackRect = accessFallback || walkOnDefaults || FIXED_DIMENSIONS;
  const rectLimits = {
    MIN_WIDTH: Number(productDimensions?.minWidth) || fallbackRect.MIN_WIDTH,
    MAX_WIDTH: Number(productDimensions?.maxWidth) || fallbackRect.MAX_WIDTH,
    MIN_LENGTH: Number(productDimensions?.minLength) || fallbackRect.MIN_LENGTH,
    MAX_LENGTH: Number(productDimensions?.maxLength) || fallbackRect.MAX_LENGTH,
    MAX_HEIGHT: Number(productDimensions?.maxHeight) || fallbackRect.MAX_HEIGHT,
    MAX_AREA: Number(productDimensions?.maxArea) || fallbackRect.MAX_AREA,
    MIN_HEIGHT_DOUBLE: fallbackRect.MIN_HEIGHT_DOUBLE,
    MIN_HEIGHT_TRIPLE: fallbackRect.MIN_HEIGHT_TRIPLE,
  };

  const minHeight = isTriple
    ? rectLimits.MIN_HEIGHT_TRIPLE
    : rectLimits.MIN_HEIGHT_DOUBLE;

  const commitDimension = (patch: Partial<Dimensions>) => {
    const nextDim = { ...state.dimensions, ...patch };

    // Enforce max area (only meaningful for rectangular products)
    if (isFixed || isWalkOn) {
      const maxArea = rectLimits.MAX_AREA;
      const area = (nextDim.width * nextDim.length) / 1_000_000;
      if (area > maxArea) {
        nextDim.length = Math.floor((maxArea * 1_000_000) / nextDim.width);
      }
    }

    updateState({ dimensions: nextDim });
  };

  const areaValue = isCircular
    ? Math.PI * Math.pow((state.dimensions.diameter || 0) / 2000, 2)
    : (state.dimensions.width * state.dimensions.length) / 1_000_000;

  // Validation \u2014 same rules as before, minus the presentation.
  const isValid = () => {
    if (isCircular) {
      const d = state.dimensions.diameter || 0;
      return d >= CIRC.MIN && d <= CIRC.MAX;
    }
    if (isFixed || isWalkOn) {
      const limits = rectLimits;
      return (
        state.dimensions.width >= limits.MIN_WIDTH &&
        state.dimensions.width <= limits.MAX_WIDTH &&
        state.dimensions.length >= limits.MIN_LENGTH &&
        state.dimensions.length <= limits.MAX_LENGTH &&
        state.dimensions.height >= minHeight &&
        state.dimensions.height <= limits.MAX_HEIGHT &&
        areaValue <= limits.MAX_AREA
      );
    }
    if (isAccess && ACCESS_LIMITS) {
      return (
        state.dimensions.width >= ACCESS_LIMITS.MIN_WIDTH &&
        state.dimensions.width <= ACCESS_LIMITS.MAX_WIDTH &&
        state.dimensions.length >= ACCESS_LIMITS.MIN_LENGTH &&
        state.dimensions.length <= ACCESS_LIMITS.MAX_LENGTH &&
        state.dimensions.height >= ACCESS_LIMITS.MIN_HEIGHT &&
        state.dimensions.height <= ACCESS_LIMITS.MAX_HEIGHT &&
        areaValue <= ACCESS_LIMITS.MAX_AREA
      );
    }
    if (isComfort && COMFORT_DIMENSIONS) {
      const maxArea = isTriple
        ? COMFORT_DIMENSIONS.MAX_AREA_TRIPLE
        : COMFORT_DIMENSIONS.MAX_AREA_DOUBLE;
      const comfortMinHeight = isTriple
        ? COMFORT_DIMENSIONS.MIN_HEIGHT_TRIPLE
        : COMFORT_DIMENSIONS.MIN_HEIGHT_DOUBLE;
      return (
        state.dimensions.width >= COMFORT_DIMENSIONS.MIN_WIDTH &&
        state.dimensions.width <= COMFORT_DIMENSIONS.MAX_WIDTH &&
        state.dimensions.length >= COMFORT_DIMENSIONS.MIN_LENGTH &&
        state.dimensions.length <= COMFORT_DIMENSIONS.MAX_LENGTH &&
        state.dimensions.height >= comfortMinHeight &&
        state.dimensions.height <= COMFORT_DIMENSIONS.MAX_HEIGHT &&
        areaValue <= maxArea
      );
    }
    return true;
  };

  // ------------- Handlers driven by inputMode ------------
  const commitWidth = (bestell: number) => commitDimension({ width: bestell });
  const commitLength = (bestell: number) => commitDimension({ length: bestell });
  const commitHeight = (h: number) => commitDimension({ height: h });
  const commitDiameter = (d: number) => commitDimension({ diameter: d });

  // In "opening" mode the user enters Rohbauöffnung (= Bestellmaß + 30 mm).
  const decode = (rawFromInput: number) =>
    inputMode === 'opening' ? Math.max(0, rawFromInput - 30) : rawFromInput;
  const encode = (bestell: number) =>
    inputMode === 'opening' ? bestell + 30 : bestell;

  const widthShown = encode(state.dimensions.width);
  const lengthShown = encode(state.dimensions.length);
  const widthMin = inputMode === 'opening'
    ? rectLimits.MIN_WIDTH + 30
    : rectLimits.MIN_WIDTH;
  const widthMax = inputMode === 'opening'
    ? rectLimits.MAX_WIDTH + 30
    : rectLimits.MAX_WIDTH;
  const lengthMin = inputMode === 'opening'
    ? rectLimits.MIN_LENGTH + 30
    : rectLimits.MIN_LENGTH;
  const lengthMax = inputMode === 'opening'
    ? rectLimits.MAX_LENGTH + 30
    : rectLimits.MAX_LENGTH;

  if (loading) {
    return (
      <div
        className="sv-hairline p-8 min-h-[240px] flex items-center sv-caps-sm text-[11px] text-ink-3"
        aria-busy="true"
      >
        Produktdaten werden geladen …
      </div>
    );
  }

  const asOpening = inputMode === 'opening';

  return (
    <div>
      <header className="mb-8 md:mb-10 max-w-2xl">
        <h2 className="sv-h2 text-ink">Wie groß soll das Fenster sein?</h2>
        <p className="sv-lede mt-3">
          {isCircular
            ? `Geben Sie den Durchmesser ein (${CIRC.MIN}–${CIRC.MAX} mm). Rechts wächst die Ansicht mit.`
            : isAccess
              ? 'Die kurze Seite bleibt 1.000 mm. Die lange Seite stellen Sie zwischen 1.000 und 1.800 mm ein.'
            : 'Breite und Länge in Millimetern. Rechts sehen Sie dasselbe Fenster als Foto, von oben, im Dach und schräg — live zu Ihren Zahlen.'}
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(320px,420px)_minmax(0,1fr)] gap-10 lg:gap-14 items-start">
        {/* ---------- LEFT: dimension inputs ---------- */}
        <div>
          {!isCircular && (
            <div className="mb-8">
              <p className="sv-field-label mb-3">Wonach messen Sie?</p>
              <InputModeToggle mode={inputMode} onChange={setInputMode} />
              <MeasureModeExplain
                mode={inputMode}
                onOpenDrawing={() => setExplainOpen(true)}
              />
            </div>
          )}

          {error && (
            <p className="mb-6 sv-serif text-[12.5px] text-ink-2">{error}</p>
          )}

          {/* --- Input rows --- */}
          <div className="border-y border-ink/40">
            {isCircular ? (
              <DimensionInput
                id="diameter"
                label="Durchmesser"
                hint="Das Maß des runden Fensters, von Kante zu Kante."
                value={state.dimensions.diameter || CIRC.MIN}
                min={CIRC.MIN}
                max={CIRC.MAX}
                step={CIRC.STEP}
                onCommit={commitDiameter}
              />
            ) : (
              <>
                <DimensionInput
                  id="width"
                  label={
                    isAccess
                      ? asOpening
                        ? 'Kurze Seite, Rohbaulichte'
                        : 'Kurze Seite'
                      : asOpening
                        ? 'Breite der Rohbaulichte'
                        : 'Breite'
                  }
                  hint={
                    isAccess
                      ? 'Beim ACCESS fest 1.000 mm — das ist die Scharnierseite.'
                      : asOpening
                        ? 'Breite der Rohbaulichte.'
                        : 'Breite des Bestellmaßes.'
                  }
                  value={widthShown}
                  min={widthMin}
                  max={widthMax}
                  step={10}
                  onCommit={(v) => commitWidth(decode(v))}
                />
                <DimensionInput
                  id="length"
                  label={
                    isAccess
                      ? asOpening
                        ? 'Lange Seite, Rohbaulichte'
                        : 'Lange Seite'
                      : asOpening
                        ? 'Länge der Rohbaulichte'
                        : 'Länge'
                  }
                  hint={
                    isAccess
                      ? asOpening
                        ? 'Rohbaulichte der langen Seite, 1.030–1.830 mm.'
                        : 'Lange Seite, 1.000–1.800 mm.'
                      : asOpening
                        ? 'Länge der Rohbaulichte.'
                        : 'Länge des Bestellmaßes.'
                  }
                  value={lengthShown}
                  min={lengthMin}
                  max={lengthMax}
                  step={10}
                  onCommit={(v) => commitLength(decode(v))}
                />
                <DimensionInput
                  id="height"
                  label="Aufbauhöhe"
                  hint="Dicke von Dämmung und Aufsatzkranz unter dem Fenster."
                  value={state.dimensions.height}
                  min={minHeight}
                  max={FIXED_DIMENSIONS.MAX_HEIGHT}
                  step={10}
                  onCommit={commitHeight}
                />
              </>
            )}
          </div>

          {!isCircular && (
            <div className="mt-6 sv-hairline-t pt-5 space-y-3">
              <div className="flex items-baseline justify-between gap-6">
                <span className="sv-field-label">
                  {MEASURE_MODE_COPY[inputMode].resultLabel}
                </span>
                <span className="sv-mono text-[16px] tabular-nums text-ink">
                  {asOpening
                    ? `${state.dimensions.width} × ${state.dimensions.length} mm`
                    : `${state.dimensions.width + 30} × ${state.dimensions.length + 30} mm`}
                </span>
              </div>
              <p className="sv-field-hint">
                {MEASURE_MODE_COPY[inputMode].resultHint}{' '}
                <button
                  type="button"
                  className="sv-measure-explain-action"
                  onClick={() => setExplainOpen(true)}
                >
                  Fotos und Zeichnung
                </button>
              </p>
            </div>
          )}

          {isAccess && !isValid() && ACCESS_LIMITS && (
            <div className="mt-6 sv-hairline p-4 border-l-2 border-l-rubric">
              <p className="sv-caps text-[11px] text-rubric mb-2">
                Maß außerhalb der ACCESS-Grenzen
              </p>
              <p className="sv-serif text-[12.5px] text-ink-2 leading-snug">
                SkyVision ACCESS hat eine feste kurze Seite von 1.000 mm und
                eine lange Seite von 1.000–1.800 mm.
              </p>
            </div>
          )}

          {isComfort && !isValid() && COMFORT_DIMENSIONS && (
            <div className="mt-6 sv-hairline p-4 border-l-2 border-l-rubric">
              <p className="sv-caps text-[11px] text-rubric mb-2">
                Konfiguration außerhalb der Toleranz
              </p>
              <p className="sv-serif text-[12.5px] text-ink-2 leading-snug">
                Die maximale Fläche für SkyVision COMFORT mit{' '}
                {isTriple ? '3-fach' : '2-fach'} Verglasung beträgt{' '}
                <span className="sv-mono text-ink">
                  {isTriple
                    ? COMFORT_DIMENSIONS.MAX_AREA_TRIPLE
                    : COMFORT_DIMENSIONS.MAX_AREA_DOUBLE}{' '}
                  m²
                </span>
                . Aktuell:{' '}
                <span className="sv-mono text-ink">
                  {areaValue.toFixed(2)} m²
                </span>
                .
              </p>
            </div>
          )}

          {(isFixed || isWalkOn) &&
            areaValue > rectLimits.MAX_AREA && (
            <div className="mt-6 sv-hairline p-4 border-l-2 border-l-rubric">
              <p className="sv-caps text-[11px] text-rubric mb-2">
                Fläche zu groß
              </p>
              <p className="sv-serif text-[12.5px] text-ink-2 leading-snug">
                Die maximale Fläche für {isWalkOn ? 'SkyVision WALKON' : 'SkyVision FIXED'} beträgt{' '}
                <span className="sv-mono text-ink">
                  {rectLimits.MAX_AREA} m²
                </span>
                .
              </p>
            </div>
          )}
        </div>

        {/* ---------- RIGHT: live drawing that scales with dimensions ---------- */}
        <div>
          <PerspectiveViewer
            kind={productKindFromName(state.product)}
            productKey={state.product}
            dimensions={{
              width: state.dimensions.width,
              length: state.dimensions.length,
              height: state.dimensions.height,
              diameter: state.dimensions.diameter,
            }}
            selected
            size="lg"
            initialView="photo"
            showOpening
          />
        </div>
      </div>

      {!isCircular && (
        <OpeningExplainDialog
          open={explainOpen}
          onClose={() => setExplainOpen(false)}
          width={state.dimensions.width}
          length={state.dimensions.length}
          mode={inputMode}
        />
      )}
    </div>
  );
}
